"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Input, Spin, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import "./DoctorFollowersPanel.css";

import { axiosFollows } from "../../../../api/axios";
import { FOLLOWS_PATHS } from "../../../../api/endpoints";
import { DOCTOR_ROUTES, ROUTES } from "../../../../routes/patientRoutes/routePaths";

import { useDoctorFollow } from "@/ProfileDoctor/hooks/useDoctorFollow";

import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { fetchDoctorById } from "../../../../authDoctor/redux/thunks/doctorsThunks";

/* ---------------- helpers ---------------- */

const toStr = (v) => (v === null || v === undefined ? "" : String(v).trim());
const isNumericId = (s) => /^\d+$/.test(String(s || ""));

const buildFullName = (u) => {
  const full = toStr(u?.fullName || u?.name);
  if (full) return full;

  const fn = toStr(u?.firstName);
  const ln = toStr(u?.lastName);
  const join = `${fn} ${ln}`.trim();
  if (join) return join;

  const email = toStr(u?.email);
  if (email.includes("@")) return email.split("@")[0];

  return "";
};

/** Generate initials from a full name, e.g. "Sedki Limam" → "SL" */
const getInitials = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const normalizeList = (raw) => {
  const data = raw?.data ?? raw;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.followers)) return data.followers;
  if (Array.isArray(data.following)) return data.following;
  if (Array.isArray(data.data?.followers)) return data.data.followers;
  if (Array.isArray(data.data?.following)) return data.data.following;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const normalizeEntityType = (type) =>
  type === "doctor" ? "doctor" : type === "user" || type === "patient" ? "patient" : "";

const normalizeFollower = (rel, profile, fallbackType = "") => {
  const id = toStr(
    profile?.id || profile?._id || profile?.userId ||
      rel?.followerId || rel?.followeeId || rel?.followee_id,
  );
  const type = normalizeEntityType(
    profile?.role || rel?.followeeType || rel?.followee_type || fallbackType,
  ) || (isNumericId(rel?.followerId || rel?.followeeId) ? "doctor" : "patient");

  const fullName = buildFullName(profile) || (type === "doctor" ? "Doctor" : "Patient");

  return {
    id,
    type,
    fullName,
    specialty: type === "doctor" ? toStr(profile?.specialty || profile?.speciality) : "",
    email: toStr(profile?.email) || "",
    raw: rel,
    user: profile || {},
  };
};

/* ---------------- Initials Avatar ---------------- */

const InitialsAvatar = ({ fullName, type }) => {
  const initials = getInitials(fullName);
  return (
    <div className={`dfp2-avatar-initials ${type === "doctor" ? "dfp2-avatar-initials--doctor" : "dfp2-avatar-initials--patient"}`}>
      {initials}
    </div>
  );
};

/* ---------------- follow-back button ---------------- */

const FollowActionButton = ({ profile, view }) => {
  const { t } = useTranslation();
  const { isFollowing, loading, toggleFollow, statusReady, canFollow } = useDoctorFollow(
    { id: profile?.id, role: profile?.type },
    profile?.type,
  );

  if (statusReady && !canFollow) return null;

  return (
    <button
      className={`dfp2-btn ${isFollowing ? "dfp2-btnGhost" : "dfp2-btnPrimary"}`}
      type="button"
      disabled={!statusReady || loading || !canFollow}
      onClick={toggleFollow}
      title={isFollowing ? "Unfollow" : "Follow"}
    >
      {!statusReady || loading
        ? "..."
        : isFollowing
          ? t("doctorProfile.following")
          : view === "followers"
            ? t("doctorProfile.followBack")
            : t("doctorProfile.follow")}
    </button>
  );
};

export default function DoctorFollowersPanel({
  doctorId,
  profileId = doctorId,
  profileType = "doctor",
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [connections, setConnections] = useState([]);

  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("followers");
  const profilesRef = useRef({});

  const resolveProfile = useCallback(
    async (entityId, typeHint = "") => {
      const id = toStr(entityId);
      if (!id) return { id, role: typeHint || "patient" };

      const tryDoctor = async () => {
        const doc = await dispatch(fetchDoctorById({ id })).unwrap();
        const fullName =
          toStr(doc?.fullName) ||
          `${toStr(doc?.firstName)} ${toStr(doc?.lastName)}`.trim() ||
          toStr(doc?.name);
        return { ...doc, id: String(doc?.id ?? doc?._id ?? id), role: "doctor", fullName };
      };

      const tryUser = async () => {
        const user = await dispatch(fetchUserById(id)).unwrap();
        const fullName =
          toStr(user?.fullName) ||
          `${toStr(user?.firstName)} ${toStr(user?.lastName)}`.trim() ||
          toStr(user?.name);
        return { ...user, id: String(user?.id ?? user?._id ?? user?.userId ?? id), role: "patient", fullName };
      };

      const first = normalizeEntityType(typeHint) || (isNumericId(id) ? "doctor" : "patient");

      try {
        return first === "doctor" ? await tryDoctor() : await tryUser();
      } catch (e1) {
        try {
          return first === "doctor" ? await tryUser() : await tryDoctor();
        } catch (e2) {
          console.warn("[Followers] Unresolved followerId:", id, { e1, e2 });
          return { id, role: first };
        }
      }
    },
    [dispatch]
  );

  const fetchConnections = useCallback(async () => {
    if (!profileId) return;

    setLoading(true);
    setErr("");

    try {
      const targetType = profileType === "patient" ? "user" : "doctor";
      const url = view === "followers"
        ? FOLLOWS_PATHS.GET_FOLLOWERS(profileId, targetType)
        : FOLLOWS_PATHS.GET_FOLLOWING(profileId, null);
      const res = await axiosFollows.get(url);
      const relations = normalizeList(res.data);

      const rows = relations.map((relation) => {
        const entityId = view === "followers"
          ? toStr(relation?.followerId)
          : toStr(relation?.followeeId || relation?.followee_id || relation?.doctorId || relation?.patientId);
        const typeHint = view === "following"
          ? normalizeEntityType(relation?.followeeType || relation?.followee_type)
          : "";
        return { relation, entityId, typeHint };
      }).filter((row) => row.entityId);

      const cache = { ...profilesRef.current };
      const missing = rows.filter(({ entityId, typeHint }) => !cache[`${typeHint || "unknown"}:${entityId}`]);

      if (missing.length) {
        const settled = await Promise.allSettled(
          missing.map(({ entityId, typeHint }) => resolveProfile(entityId, typeHint)),
        );
        settled.forEach((s, idx) => {
          const { entityId, typeHint } = missing[idx];
          const cacheKey = `${typeHint || "unknown"}:${entityId}`;
          if (s.status === "fulfilled" && s.value) cache[cacheKey] = s.value;
        });
        profilesRef.current = cache;
      }

      const normalized = rows.map(({ relation, entityId, typeHint }) =>
        normalizeFollower(
          view === "followers" ? relation : { ...relation, followerId: entityId },
          cache[`${typeHint || "unknown"}:${entityId}`],
          typeHint,
        ),
      ).filter((item) => !!item.id);

      const seen = new Set();
      const uniq = [];
      for (const it of normalized) {
        const key = `${it.type}-${it.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        uniq.push(it);
      }

      setConnections(uniq);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load followers";
      setErr(String(msg));
      console.error("❌ Error fetching followers:", e);
    } finally {
      setLoading(false);
    }
  }, [profileId, profileType, resolveProfile, view]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const counts = useMemo(() => {
    const doctors = connections.filter((f) => f.type === "doctor").length;
    const patients = connections.filter((f) => f.type === "patient").length;
    return { all: connections.length, doctors, patients };
  }, [connections]);

  const filtered = useMemo(() => {
    const query = toStr(q).toLowerCase();
    return connections
      .filter((f) => {
        if (tab === "doctors") return f.type === "doctor";
        if (tab === "patients") return f.type === "patient";
        return true;
      })
      .filter((f) => {
        if (!query) return true;
        const hay = `${f.fullName} ${f.specialty} ${f.email}`.toLowerCase();
        return hay.includes(query);
      });
  }, [connections, tab, q]);

  const openProfile = (f) => {
    if (f.type === "doctor") {
      const cleanDoctorId = String(f.id || "").replace(/^DR_/, "");
      if (cleanDoctorId) navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(cleanDoctorId));
      return;
    }
    if (f.id) navigate(ROUTES.PROFILE(f.id));
  };

  const getDisplayName = (f) => {
    if (f.type === "doctor") return `Dr. ${f.fullName}`;
    return f.fullName;
  };

  return (
    <section className="dfp2-card">
      <header className="dfp2-head">
        <div className="dfp2-titleWrap">
          <h3 className="dfp2-title">
            {view === "followers"
              ? t("doctorProfile.followersTitle")
              : t("doctorProfile.followingTitle")}
          </h3>
          <p className="dfp2-sub">
            {view === "followers"
              ? t("doctorProfile.followersSubtitle")
              : t("doctorProfile.followingSubtitle")}
          </p>
        </div>
      </header>

      <div className="dfp2-viewSwitch" role="tablist">
        {[
          ["followers", t("doctorProfile.followersTitle")],
          ["following", t("doctorProfile.followingTitle")],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={`dfp2-pill ${view === key ? "isActive" : ""}`}
            onClick={() => { setView(key); setTab("all"); }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dfp2-stats">
        <button type="button" className={`dfp2-pill ${tab === "all" ? "isActive" : ""}`} onClick={() => setTab("all")}>
          {t("doctorProfile.all")}
          <span className="dfp2-badge">{counts.all}</span>
        </button>

        <button type="button" className={`dfp2-pill ${tab === "doctors" ? "isActive" : ""}`} onClick={() => setTab("doctors")}>
          {t("doctorProfile.doctors")}
          <span className="dfp2-badge">{counts.doctors}</span>
        </button>

        <button type="button" className={`dfp2-pill ${tab === "patients" ? "isActive" : ""}`} onClick={() => setTab("patients")}>
          {t("doctorProfile.patients")}
          <span className="dfp2-badge">{counts.patients}</span>
        </button>
      </div>

      <div className="dfp2-search">
        <Input
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          prefix={<SearchOutlined />}
          placeholder={t("doctorProfile.searchFollowers")}
        />
      </div>

      {err ? (
        <div className="dfp2-error">
          <div className="dfp2-errorText">{err}</div>
          <button className="dfp2-btn dfp2-btnGhost" type="button" onClick={fetchConnections}>
            {t("doctorFeed.retry")}
          </button>
        </div>
      ) : loading ? (
        <div className="dfp2-loading">
          <Spin />
          <span>{t("common.loading")}</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="dfp2-empty">
          <Empty
            description={t(
              view === "followers"
                ? "doctorProfile.noFollowers"
                : "doctorProfile.noFollowing",
            )}
          />
        </div>
      ) : (
        <div className="dfp2-list">
          {filtered.map((f) => (
            <div key={`${f.type}-${f.id}`} className="dfp2-row">
              <button className="dfp2-rowMain" type="button" onClick={() => openProfile(f)}>
                {/* ✅ Initials avatar — no image */}
                <InitialsAvatar fullName={f.fullName} type={f.type} />

                <div className="dfp2-meta">
                  <div className="dfp2-nameLine">
                    {/* ✅ Dr. prefix for doctors */}
                    <span className="dfp2-name">{getDisplayName(f)}</span>
                  </div>

                  {f.specialty ? (
                    <div className="dfp2-subLine">{f.specialty}</div>
                  ) : f.email ? (
                    <div className="dfp2-subLine">{f.email}</div>
                  ) : (
                    <div className="dfp2-subLine">—</div>
                  )}
                </div>
              </button>
              <div className="dfp2-actions">
                <FollowActionButton profile={f} view={view} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}